import { useEffect } from "react";

import { Alert, ScrollView, View } from "react-native";
import {
  Button,
  Card,
  Divider,
  IconButton,
  Text,
  TouchableRipple,
  useTheme,
} from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import { FormikFormSwitch } from "../components/FormSwitch";
import ManageVariantsInventory from "../components/ManageVariantsInventory";
import VariantOptions from "../components/VariantOptions";
import useProductFormFormik from "../hooks/useProductFormFormik";
import { TabScreenProps, productFormRoutes } from "../navigation/types";
import { VariantWithOptions } from "../types";
import { confirmAlert } from "../utils";

type Props = TabScreenProps<typeof productFormRoutes.tabs.variants>;
export default function VariantsScreen({ navigation }: Props) {
  const {
    values: { variants, inventory },
    setFieldValue,
  } = useProductFormFormik();

  const tailwind = useTailwind();

  const navigateToVariantForm = (selectedVariant?: string) => {
    navigation.navigate(productFormRoutes.variantForm, { selectedVariant });
  };

  const showAlert = () => {
    confirmAlert({
      title: "Activar variantes de tu producto",
      body: "Se desactivará el inventario simple de tu producto.",
      cancelButton: {
        text: "Regresar",
        onPress: () => setFieldValue("variants.enabled", false),
      },
      confirmButton: {
        text: "Activar",
        onPress: () => setFieldValue("inventory.enabled", false),
      },
    });
  };

  useEffect(() => {
    if (variants.enabled && inventory.enabled) {
      showAlert();
    }
  }, [variants.enabled]);

  const title = `${
    variants.enabled && !inventory.enabled ? "Desactivar" : "Activar"
  } variantes`;

  const body = `Pulsa aquí para ${
    variants.enabled && !inventory.enabled ? "desactivar" : "activar"
  } variantes de tu producto.`;

  return (
    <ScrollView>
      <Card style={tailwind("m-3 mb-24 rounded-xl")}>
        <Card.Content>
          <FormikFormSwitch
            fieldName="variants.enabled"
            title={title}
            body={body}
          />

          {variants.enabled && (
            <View>
              <Button
                mode="contained-tonal"
                style={tailwind("my-4 mx-8")}
                onPress={() => navigateToVariantForm()}
              >
                Crear nueva variante
              </Button>

              {!!variants.variantsWithOptions.length && (
                <VariantsWithOptions
                  variantsWithOptions={variants.variantsWithOptions}
                  navigateToEditVariant={navigateToVariantForm}
                />
              )}
            </View>
          )}
        </Card.Content>
      </Card>
    </ScrollView>
  );
}

const VariantsWithOptions = ({
  variantsWithOptions,
  navigateToEditVariant,
}: {
  variantsWithOptions: VariantWithOptions[];
  navigateToEditVariant: (variant: string) => void;
}) => {
  const { colors } = useTheme();
  const tailwind = useTailwind();

  return (
    <View>
      <Text variant="titleMedium">Variantes y opciones</Text>
      <View style={tailwind("my-4")}>
        {variantsWithOptions.map(({ variant, options }, index) => (
          <View key={index}>
            <TouchableRipple
              onPress={() => navigateToEditVariant(variant.name)}
            >
              <View style={tailwind("flex-row items-center justify-between")}>
                <View style={tailwind("flex-1 flex-row")}>
                  <View style={tailwind("mr-1")}>
                    <Text variant="bodyMedium">Variante:</Text>
                    <Text variant="bodyMedium">Opciones:</Text>
                  </View>
                  <View style={tailwind("flex-1")}>
                    <Text
                      variant="bodyMedium"
                      style={tailwind("font-semibold")}
                      numberOfLines={1}
                    >
                      {variant.name}
                    </Text>
                    <VariantOptions options={options} />
                  </View>
                </View>
                <IconButton icon="menu-right" iconColor={colors.primary} />
              </View>
            </TouchableRipple>

            <Divider style={tailwind("my-1")} />
          </View>
        ))}
      </View>

      <ManageVariantsInventory />
    </View>
  );
};
